import { useState, useEffect, ReactElement } from 'react'
import { t } from '@lingui/core/macro'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import { Link } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { ExportList } from '@components/buttons/exportList'
import { ImportList } from '@components/buttons/importList'
import { Search } from '@components/layout/toolbars/tableToolbar/search'
import { Button } from '@components/v2/button'
import { useAppSelector } from '@redux/hooks'
import { ErrorsTooltip } from './errorsTooltip'

interface TableHeaderProps {
  onSearchCallback?: (value: string) => void
  template?: any
  lastSavedText?: string | ReactElement | null
  errors?: any
  errorCount?: number
  onErrorClick?: () => void
  tableRef?: any
  noBadge?: boolean
  type?: 'list'
  poweredBy?: { id: string; name: string } | null
  setReadOnlyCallback?: (status: boolean) => void
  isApproverMode: boolean
  handleCommentsButton: () => void
  isSidePanelOpen: boolean
}

const poweredTemplateName = (name: string) => t`Powered by Template: ${name}`

const emptyFn = () => {
  // do nothing
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  onSearchCallback,
  template,
  lastSavedText = null,
  errors,
  errorCount = 0,
  onErrorClick = emptyFn,
  tableRef,
  noBadge,
  type,
  poweredBy,
  setReadOnlyCallback = emptyFn,
  isApproverMode = true,
  handleCommentsButton,
  isSidePanelOpen,
}) => {
  const [LIST_IMPORT_EXPORT = false, ENABLE_LIVEBLOCKS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.LIST_IMPORT_EXPORT, FeatureDict.ENABLE_LIVEBLOCKS]),
  )
  const [fieldsById, setFieldsById] = useState({})

  useEffect(() => {
    setFieldsById(
      template?.dependencies?.columns?.reduce((memo, item) => {
        memo[item.field_id] = item
        return memo
      }, {}),
    )
  }, [template?.dependencies?.columns])

  const onSearch = e => {
    if (onSearchCallback) {
      onSearchCallback(e.target.value)
      return
    }

    if (e.key !== 'Enter') return

    // this logic is only going to work with handsontable. Read Table needs it's own search.
    const searchTerm = e.target.value
    const currentSelection = tableRef?.getSelectedLast()
    const startingPoint = currentSelection ? currentSelection[0] : 0
    const startingCol = currentSelection ? currentSelection[1] + 1 : 0
    let exit = false
    const data = tableRef.getData()
    for (let index = startingPoint; index < data.length; index++) {
      for (let colIdx = 0; colIdx < data[index].length; colIdx++) {
        if (index === startingPoint && colIdx < startingCol) {
          // wasted column
        } else if (
          data[index][colIdx]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        ) {
          tableRef.selectCell(index, colIdx)
          tableRef.unlisten()
          exit = true
          break
        }
      }
      if (exit) {
        break
      }
    }

    if (!exit) {
      let secondExit = false
      for (let index = 0; index < startingPoint; index++) {
        for (let colIdx = 0; colIdx < data[index].length; colIdx++) {
          if (index === startingPoint && colIdx < startingCol) {
            // wasted column
          } else if (
            data[index][colIdx]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          ) {
            tableRef.selectCell(index, colIdx)
            tableRef.unlisten()
            secondExit = true
            break
          }
        }
        if (secondExit) {
          break
        }
      }
    }
    const { target } = e
    e.stopPropagation()
    target.focus()
    setTimeout(() => {
      target.focus()
      target.click()
    }, 200)
    setTimeout(() => {
      target.focus()
      target.click()
    }, 100)
  }

  const list = template // "template" does not make sense in this context; it's a list.

  return (
    <div
      style={{
        height: '40px',
        backgroundColor: 'white',
        display: 'flex',
        borderTop: '1px solid #ccc',
        lineHeight: '40px',
        padding: '0 24px',
        color: 'rgba(0, 0, 0, 0.54)',
      }}
    >
      <div style={{ display: 'flex', width: '100%', gap: '15px', alignItems: 'center' }}>
        <div>{lastSavedText}</div>
        {list?.id && LIST_IMPORT_EXPORT ? (
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              gap: '15px',
              height: '100%',
              alignItems: 'center',
            }}
          >
            <ExportList listId={list.id} style={{ height: 35 }} />
            {isApproverMode && (
              <ImportList
                listId={list.id}
                listName={list.name}
                listDescription={list.notes}
                listType={list.type}
                setReadOnlyCallback={setReadOnlyCallback}
              />
            )}
            {ENABLE_LIVEBLOCKS && (
              <Button
                variant={isSidePanelOpen ? 'dark' : 'secondary'}
                onClick={handleCommentsButton}
                style={{ height: 35 }}
              >
                {t`Comments`}
              </Button>
            )}
          </div>
        ) : null}
        {type === 'list' && poweredBy ? (
          <Link
            to={`/admin/templates/edit-template/${poweredBy.id}/setup`}
            style={{ marginLeft: 24, color: 'rgb(0, 124, 255)' }}
          >
            <FlashOnIcon style={{ paddingTop: 3, paddingBottom: 3 }} />
            {poweredTemplateName(poweredBy.name)}
          </Link>
        ) : null}
        <div
          style={{
            textAlign: 'right',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          {!noBadge && (
            <ErrorsTooltip
              errors={errors}
              errorCount={errorCount}
              onErrorClick={onErrorClick}
              fieldsById={fieldsById}
            />
          )}
          <Search
            onSearchCallback={onSearchCallback}
            onKeyPress={onSearch}
            onChange={onSearch}
            searchBoxStyles={{ height: 35 }}
            inputBoxStyles={{ height: 35, backgroundColor: 'transparent' }}
          />
        </div>
      </div>
    </div>
  )
}
